import { mergeAttributes, Node } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion from '@tiptap/suggestion';
import { ReactNodeViewRenderer } from '@tiptap/react';
import BookmarkComponent from './BookmarkComponent';

const MentionPluginKey = new PluginKey('bookmark');

const Bookmark = Node.create({
	name: 'bookmark',

	group: 'inline',

	inline: true,

	atom: true,

	draggable: true,

	selectable: true,

	addOptions() {
		return {
			suggestion: {
				char: '#',
				pluginKey: MentionPluginKey,
				command: ({ range, props }) => {
					const event = new CustomEvent('insert-bookmark', { detail: { range, ...props } });

					document.dispatchEvent(event);
				},
				allow: ({ state, range }) => {
					const $from = state.doc.resolve(range.from);
					const type = state.schema.nodes[this.name];
					return !!$from.parent.type.contentMatch.matchType(type);
				},
			},
		};
	},

	addAttributes() {
		return {
			code: {
				default: null,
				parseHTML: element => element.getAttribute('data-code'),
				renderHTML: attributes => {
					if (!attributes.code) {
						return {};
					}

					return {
						'data-code': attributes.code,
					};
				},
			},
			display: {
				default: null,
				parseHTML: element => element.getAttribute('data-display'),
				renderHTML: attributes => {
					if (!attributes.display) {
						return {};
					}

					return {
						'data-display': attributes.display,
						title: attributes.display,
					};
				},
			},
			value: {
				default: null,
				parseHTML: element => element.getAttribute('data-value'),
				renderHTML: attributes => {
					if (!attributes.value) {
						return {};
					}

					return {
						'data-value': attributes.value,
					};
				},
			},
			// Added hidden attribute to hide Initial Cursor position bookmark on report
			hidden: {
				default: null,
				parseHTML: element => element.getAttribute('data-hidden'),
				renderHTML: attributes => {
					if (!attributes.hidden) {
						return {};
					}

					return {
						'data-hidden': attributes.hidden,
					};
				},
			},

			exdconc: {
				default: null,
				parseHTML: element => element.getAttribute('data-exd-conc'),
				renderHTML: attributes => {
					if (!attributes.exdconc) {
						return {};
					}

					return {
						'data-exd-conc': attributes.exdconc,
					};
				},
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: `span[data-type="${this.name}"]`,
			},
		];
	},

	renderHTML({ node, HTMLAttributes }) {
		return [
			'span',
			mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { 'data-type': this.name }),
			node.attrs.value ?? node.attrs.display,
		];
	},

	renderText({ node }) {
		return node.attrs.value ?? node.attrs.display;
	},

	addProseMirrorPlugins() {
		return [
			Suggestion({
				editor: this.editor,
				...this.options.suggestion,
			}),
		];
	},

	// Render's Custom Bookmark tag Component with tooltip
	addNodeView() {
		return ReactNodeViewRenderer(BookmarkComponent, {
			attrs: {
				'data-drag-handle': this.name,
			},
		});
	},
});

export default Bookmark;
